<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import ArtCollection from '@/components/modals/Art-collection.vue';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons-vue';

const b = bem('collections-grid');

export default {
  components: {
    ArtCollection,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    PictureOutlined,
  },
  props: {
    collections: { type: Array, default: () => [] },
  },
  emits: ['select', 'updated'],
  data() {
    return {
      showModal: false,
      isNew: false,
      editingCollection: {},
    };
  },
  methods: {
    ...mapActions({
      deleteAction: 'createTemplate/deleteTemplateCollection',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    onCardClick(collection) {
      this.$emit('select', collection);
    },
    onEdit(e, collection) {
      e.stopPropagation();
      this.editingCollection = collection;
      this.isNew = false;
      this.showModal = true;
    },
    onCreate() {
      this.editingCollection = {};
      this.isNew = true;
      this.showModal = true;
    },
    async onDelete(collection) {
      await this.deleteAction(collection.id);
      this.$emit('updated');
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('header')">
      <h2 :class="b('title')">Collections</h2>
      <a-button type="primary" @click="onCreate">
        <template #icon><PlusOutlined /></template>
        New collection
      </a-button>
    </div>

    <div :class="b('grid')">
      <!-- Collection cards -->
      <div
        v-for="collection in collections"
        :key="collection.id"
        :class="b('card')"
        role="button"
        tabindex="0"
        @click="onCardClick(collection)"
        @keydown.enter="onCardClick(collection)"
      >
        <div :class="b('card-image')">
          <img
            v-if="collection.imageUri"
            :src="collection.imageUri"
            :alt="collection.name"
          />
          <div v-else :class="b('card-placeholder')">
            <PictureOutlined />
          </div>
        </div>

        <div :class="b('card-body')">
          <h3 :class="b('card-title')">
            {{ collection.name }}
          </h3>
          <p
            v-if="collection.description"
            :class="b('card-desc')"
          >
            {{ collection.description }}
          </p>
        </div>

        <div :class="b('card-footer')">
          <a-button
            type="text"
            size="small"
            @click="onEdit($event, collection)"
          >
            <template #icon><EditOutlined /></template>
          </a-button>
          <a-popconfirm
            title="Delete this collection?"
            ok-text="Yes"
            cancel-text="No"
            @confirm="onDelete(collection)"
          >
            <a-button
              type="text"
              size="small"
              danger
              @click.stop
            >
              <template #icon><DeleteOutlined /></template>
            </a-button>
          </a-popconfirm>
        </div>
      </div>
    </div>

    <ArtCollection
      v-if="showModal"
      v-model:show="showModal"
      :collection="editingCollection"
      :is-new="isNew"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.collections-grid {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: var(--Text-Primary);
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
  }

  &__card {
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 12px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;

    &:hover {
      border-color: var(--Button-Primary);
      box-shadow: var(--Shadow-Medium);
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 2px solid var(--Button-Primary);
      outline-offset: 2px;
    }
  }

  &__card-image {
    width: 100%;
    height: 160px;
    overflow: hidden;
    background: var(--Background-Tertiary);

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__card-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 40px;
    color: var(--Text-Secondary);
    opacity: 0.4;
  }

  &__card-body {
    padding: 16px;
    flex: 1;
  }

  &__card-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0 0 4px;
  }

  &__card-desc {
    font-size: 13px;
    color: var(--Text-Secondary);
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__card-footer {
    display: flex;
    gap: 4px;
    padding: 8px 12px;
    border-top: 1px solid var(--Border-Light);
  }
}
</style>
